import { hasVaultAccess, isOwner } from 'helpers/utils';
import {
  MyAlbumAccess,
  MyVaultAccess,
  ShareRole,
  ShareTarget,
} from 'networking/controllers/shares-controller';

// What the signed-in person may do in a vault or album, from the API's
// my_access (api/app/services/sharing/access.rb). Vaults fetched without it
// (lists, old screens) fall back to owner/editor = everything, else nothing.

export const vaultAccess = (
  user: UserType,
  vault?: VaultType | null,
): MyVaultAccess => {
  if (vault?.myAccess) return vault.myAccess;
  const owner = Boolean(vault && isOwner(user, vault));
  const full = Boolean(vault && hasVaultAccess(user, vault));
  let role: MyVaultAccess['role'] = null;
  if (owner) role = 'owner';
  else if (full) role = 'editor';
  return {
    role,
    fullAccess: full,
    canManageVault: full,
    canManageBilling: owner,
    canShareVault: full,
    canCreateAlbum: full,
    albumRoles: {},
    sharedContentIds: [],
  };
};

const ADDING_ROLES = ['editor', 'contributor', 'limited_contributor'];

/**
 * Whether the person adds to the vault: its owner, its editors and
 * contributors, and editors and contributors of an album in it. They get both
 * "My Stuff" and "Shared With Me" there; everyone else (viewers, album or
 * memory viewers) only "Shared With Me" (see the vault tabs bar).
 */
export const addsToVault = (access: MyVaultAccess) =>
  access.role === 'owner' ||
  ADDING_ROLES.includes(access.role || '') ||
  Object.values(access.albumRoles).some((role) =>
    ADDING_ROLES.includes(role || ''),
  );

const FULL_ALBUM_ACCESS: MyAlbumAccess = {
  role: 'editor',
  canUpload: true,
  canCreatePrompt: true,
  canEdit: true,
  canDelete: true,
  canReorder: true,
  canShare: true,
  seesPrivate: true,
  mine: false,
};

const NO_ALBUM_ACCESS: MyAlbumAccess = {
  role: null,
  canUpload: false,
  canCreatePrompt: false,
  canEdit: false,
  canDelete: false,
  canReorder: false,
  canShare: false,
  seesPrivate: false,
  mine: false,
};

export const albumAccess = (
  album: { myAccess?: MyAlbumAccess } | null | undefined,
  inVault: MyVaultAccess,
): MyAlbumAccess => {
  if (album?.myAccess) return album.myAccess;
  return inVault.fullAccess ? FULL_ALBUM_ACCESS : NO_ALBUM_ACCESS;
};

/**
 * Whether the person may change or delete a memory: an editor of its album,
 * or its contributor who added it.
 */
export const canEditMemory = (
  memory: { createdBy?: number | null; createdByUser?: { id?: number } | null },
  inAlbum: MyAlbumAccess,
  user: UserType,
) => {
  if (inAlbum.role === 'editor') return true;
  if (!['contributor', 'limited_contributor'].includes(inAlbum.role || '')) {
    return false;
  }
  const creatorId = memory.createdBy ?? memory.createdByUser?.id;
  return Boolean(user.id && creatorId === user.id);
};

/** A collaborator picked while creating a vault, as an invitation role. */
export const vaultInvitationRole = (contributor: {
  role?: string;
  canViewVault?: boolean;
}): ShareRole => {
  if (contributor.role === 'viewer') return 'viewer';
  if (contributor.role === 'contributor') {
    return contributor.canViewVault === false
      ? 'limited_contributor'
      : 'contributor';
  }
  return 'editor';
};

export const vaultShareTarget = (vault: VaultType): ShareTarget => ({
  type: 'vault',
  vaultId: vault.id,
  id: vault.id,
  name: vault.title,
});

export const albumShareTarget = (
  vaultId: number,
  album: { id: number; name: string; isPrivate?: boolean },
): ShareTarget => ({
  type: 'album',
  vaultId,
  id: album.id,
  name: album.name,
  isPrivate: album.isPrivate,
});
